import { Button, EmptyState } from '@mevaser/ui'
import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
  useNavigate,
} from '@tanstack/react-router'
import { AlertTriangle, FileQuestion } from 'lucide-react'
import { useEffect, useState } from 'react'
import { AppShell } from './components/app-shell.js'
import { CommandPalette } from './components/command-palette.js'
import { useAuth } from './lib/auth.js'
import { AttributesPage } from './routes/attributes.js'
import { CamerasPage } from './routes/cameras.js'
import { CategoriesPage } from './routes/categories.js'
import { CompatibilityPage } from './routes/compatibility.js'
import { CouponsPage } from './routes/coupons.js'
import { GiftCardsPage } from './routes/gift-cards.js'
import { ImageLibraryPage } from './routes/image-library.js'
import { CustomerCleanupPage } from './routes/customer-cleanup.js'
import { CustomerDetailPage } from './routes/customer-detail.js'
import { CustomersPage } from './routes/customers.js'
import { DashboardPage } from './routes/dashboard.js'
import { DownloadsPage } from './routes/downloads.js'
import { ForgotPasswordPage } from './routes/forgot-password.js'
import { IntakePage } from './routes/intake.js'
import { KiosksPage } from './routes/kiosks.js'
import { LabelsPage } from './routes/labels.js'
import { LocationsPage } from './routes/locations.js'
import { LoginPage } from './routes/login.js'
import { MachineDetailPage } from './routes/machine-detail.js'
import { MachinesPage } from './routes/machines.js'
import { MailPage } from './routes/mail.js'
import { NewOrderPage } from './routes/new-order.js'
import { OrderDetailPage } from './routes/order-detail.js'
import { OrdersPage } from './routes/orders.js'
import { PlansPage } from './routes/plans.js'
import { ProductsPage } from './routes/products.js'
import { ProvisioningPage } from './routes/provisioning.js'
import { PurchaseOrderDetailPage } from './routes/purchase-order-detail.js'
import { PurchaseOrdersPage } from './routes/purchase-orders.js'
import { PurchasingPage } from './routes/purchasing.js'
import { RentalDetailPage } from './routes/rental-detail.js'
import { RentalsPage } from './routes/rentals.js'
import { RepairDetailPage } from './routes/repair-detail.js'
import { RepairsPage } from './routes/repairs.js'
import { ResetPasswordPage } from './routes/reset-password.js'
import { SettingsPage } from './routes/settings.js'
import { StaffPage } from './routes/staff.js'
import { PaymentsPage } from './routes/payments.js'
import { TimeClockPage } from './routes/time-clock.js'
import { StockPage } from './routes/stock.js'
import { StockRequestDetailPage } from './routes/stock-request-detail.js'
import { StockRequestsPage } from './routes/stock-requests.js'
import { SupportChatPage } from './routes/support-chat.js'
import { SupportTicketDetailPage } from './routes/support-ticket-detail.js'
import { SupportTicketsPage } from './routes/support-tickets.js'
import { TaskDetailPage } from './routes/task-detail.js'
import { TasksPage } from './routes/tasks.js'
import { VendorDetailPage } from './routes/vendor-detail.js'
import { VendorsPage } from './routes/vendors.js'
import { WikiPage } from './routes/wiki.js'
import { WikiPageDetailPage } from './routes/wiki-page.js'
import { WorkspacePage } from './routes/workspace.js'

const rootRoute = createRootRoute({
  component: () => <Outlet />,
  notFoundComponent: () => {
    const navigate = useNavigate()
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <EmptyState
          icon={FileQuestion}
          title="Page not found"
          description="That page does not exist, or you may not have access to it."
          action={{ label: 'Go to dashboard', onClick: () => void navigate({ to: '/' }) }}
        />
      </div>
    )
  },
  errorComponent: ({ error }) => (
    <div className="flex min-h-dvh items-center justify-center">
      <EmptyState
        icon={AlertTriangle}
        title="Something went wrong"
        description={error instanceof Error ? error.message : 'An unexpected error occurred.'}
        action={{ label: 'Reload', onClick: () => window.location.reload() }}
      />
    </div>
  ),
})

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
})

// Signed-out siblings of /login. They sit outside the authed layout for the
// obvious reason: someone who has forgotten their password cannot be asked to
// be signed in first. Keep them in step with PUBLIC_PATHS in main.tsx, which
// is what stops a 401 from any background call bouncing them to /login
// mid-flow — on /reset-password that would throw the emailed token away.
const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/forgot-password',
  component: ForgotPasswordPage,
})

const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reset-password',
  component: ResetPasswordPage,
})

/**
 * Everything inside the shell requires a full session. The guard runs in the
 * layout rather than per page, so a new page cannot be added without it.
 */
function AuthedLayout() {
  const { staff, pending2fa, isLoading, isRefreshing } = useAuth()
  const [paletteOpen, setPaletteOpen] = useState(false)
  const navigate = useNavigate()

  // Redirect from an effect, never during render: navigating mid-render updates
  // the router while this component is still rendering, which React warns about
  // and which can drop the redirect entirely.
  //
  // The isRefreshing guard matters just after the 2FA code is verified, when
  // the cached answer still says "pending 2FA" — acting on it would bounce
  // the user back to sign-in the moment they finished.
  const needsLogin = !isLoading && !isRefreshing && (!staff || pending2fa)

  useEffect(() => {
    if (needsLogin) void navigate({ to: '/login' })
  }, [needsLogin, navigate])

  // Show the spinner both while loading and while the redirect is in flight, so
  // the shell never flashes for someone who is about to be sent to sign-in.
  if (isLoading || !staff || pending2fa) {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <div className="size-5 animate-spin rounded-full border-2 border-border border-t-primary" />
      </div>
    )
  }

  return (
    <>
      <AppShell onOpenCommandPalette={() => setPaletteOpen(true)}>
        <Outlet />
      </AppShell>
      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
    </>
  )
}

const authedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'authed',
  component: AuthedLayout,
})

// Generic over the path so the literal type survives: without it, every
// <Link to="/orders"> would fail to typecheck because the router would only
// know about the routes declared inline.
const makeRoute = <P extends string>(path: P, component: () => React.JSX.Element) =>
  createRoute({ getParentRoute: () => authedRoute, path, component })

// The one route with search params so far, so it's declared with createRoute
// directly rather than through makeRoute. `from` is the in-app location to
// return to once the chat is closed; anything that isn't a same-app path is
// dropped ("//evil.com" is protocol-relative, hence the second check) rather
// than rejected, since a bad value should just fall back to "/", not 404.
const supportChatRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/support-chat',
  component: SupportChatPage,
  validateSearch: (search: Record<string, unknown>): { from?: string } => {
    const from = search.from
    return typeof from === 'string' && from.startsWith('/') && !from.startsWith('//')
      ? { from }
      : {}
  },
})

const routeTree = rootRoute.addChildren([
  loginRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
  authedRoute.addChildren([
    makeRoute('/', DashboardPage),
    makeRoute('/products', ProductsPage),
    makeRoute('/compatibility', CompatibilityPage),
    makeRoute('/categories', CategoriesPage),
    makeRoute('/coupons', CouponsPage),
    makeRoute('/gift-cards', GiftCardsPage),
    makeRoute('/attributes', AttributesPage),
    makeRoute('/image-library', ImageLibraryPage),
    makeRoute('/intake', IntakePage),
    makeRoute('/labels', LabelsPage),
    makeRoute('/provisioning', ProvisioningPage),
    makeRoute('/stock', StockPage),
    makeRoute('/purchasing', PurchasingPage),
    makeRoute('/purchase-orders', PurchaseOrdersPage),
    makeRoute('/purchase-orders/$orderId', PurchaseOrderDetailPage),
    makeRoute('/stock-requests', StockRequestsPage),
    makeRoute('/stock-requests/$requestId', StockRequestDetailPage),
    makeRoute('/locations', LocationsPage),
    makeRoute('/vendors', VendorsPage),
    makeRoute('/vendors/$vendorId', VendorDetailPage),
    makeRoute('/customers', CustomersPage),
    // Registered before the dynamic segment so "cleanup" is a page, not an id.
    makeRoute('/customers/cleanup', CustomerCleanupPage),
    makeRoute('/customers/$customerId', CustomerDetailPage),
    makeRoute('/repairs', RepairsPage),
    makeRoute('/repairs/$ticketId', RepairDetailPage),
    makeRoute('/rentals', RentalsPage),
    makeRoute('/rentals/$rentalId', RentalDetailPage),
    makeRoute('/plans', PlansPage),
    makeRoute('/support-tickets', SupportTicketsPage),
    makeRoute('/tasks', TasksPage),
    makeRoute('/tasks/$taskId', TaskDetailPage),
    makeRoute('/support-tickets/$ticketId', SupportTicketDetailPage),
    // The vendor assistant embed's full-page view — unrelated to the human
    // support-tickets feature above.
    supportChatRoute,
    makeRoute('/mail', MailPage),
    makeRoute('/orders', OrdersPage),
    // Registered before the dynamic segment so "new" is a page, not an id.
    makeRoute('/orders/new', NewOrderPage),
    makeRoute('/orders/$orderId', OrderDetailPage),
    makeRoute('/kiosks', KiosksPage),
    makeRoute('/cameras', CamerasPage),
    makeRoute('/machines', MachinesPage),
    makeRoute('/machines/$machineId', MachineDetailPage),
    makeRoute('/downloads', DownloadsPage),
    makeRoute('/wiki', WikiPage),
    makeRoute('/wiki/$pageId', WikiPageDetailPage),
    makeRoute('/workspace', WorkspacePage),
    makeRoute('/staff', StaffPage),
    makeRoute('/time-clock', TimeClockPage),
    makeRoute('/payments', PaymentsPage),
    makeRoute('/settings', SettingsPage),
  ]),
])

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0,
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

export { Button, redirect }
